'use strict';
import api from 'utils/api';
import store from 'store';
import Immutable from 'immutable';
import flatten from 'lodash/array/flatten';
import queryString from 'query-string';
import { encodeUrl } from '../utils/strings';
import cookie from "js-cookie";

const MediaSource = (alt) => {
    return {
        loadMedia: {
            local(state, params) {
                var cached = state._cache[JSON.stringify(params)] || null;
                if (Object.getOwnPropertyNames(params).length == 0 || (params.hasOwnProperty('clearFlag') && !!params['clearFlag'])) {
                    alt.getActions('media').clearQuery();
                }

                if (cached) {
                    alt.getActions('media').loadMediaSuccess(cached);
                    return true;
                }
            },
            remote(state, params) {
                return api.get('media', params).then((data) => {
                    return {data, params};
                });
            },
            success: alt.getActions('media').loadMediaSuccess,
            error: alt.getActions('media').loadMediaError
        },
        loadMediaModals: {
            local(state, params) {
                var cached = state._cache[JSON.stringify(params)] || null;
                if (cached) {
                    alt.getActions('media').loadMediaModalsSuccess(cached);
                    return true;
                }
            },
            remote(state, params) {
                return api.get('media', params).then((data) => {
                    return {data, params};
                });
            },
            success: alt.getActions('media').loadMediaModalsSuccess,
            error: alt.getActions('media').loadMediaError
        },
        loadLandingPage: {
            local(state) {
                return state.landingPage.size !== 0 ? state.landingPage.toJS() : null;
            },
            remote() {
                return api.get('landing_pages');
            },
            success: alt.getActions('media').loadLandingPageSuccess,
            error: alt.getActions('media').loadLandingPageError
        },
        checkQuota: {
            remote(state, params) {
                return api.post('request_download', params);
            },
            success: alt.getActions('media').checkQuotaSuccess,
            error: alt.getActions('media').checkQuotaError
        }
    };
};

export default class MediaStore {
    constructor() {
        this.bindActions( this.alt.getActions('media') );
        this.registerAsync( MediaSource );

        // Make the UI even more Snappy
        // by caching media query during the session
        this._cache = {};

        // Exposed
        this.media = Immutable.fromJS([]);
        this.mediaModal = Immutable.fromJS([]);
        this.filters = Immutable.fromJS([]);
        this.landingPage = Immutable.fromJS({});
        this.selectedFilters = Immutable.fromJS([]);
        this.meta = Immutable.fromJS({});
        this.slotDefinitions = Immutable.fromJS([]);
        this.modalMeta = Immutable.fromJS({});
        this.params = {};
        this.isExpanded = false;
        this.quotaExceeded = false;
        this.nextElement = false;
        this.previousElement = false;
        this.fetchPreviousPage = false;
        this.fetchNextPage = false;
        this.slotConfig = window.config.detail_view;
        this.modalOrientation = window.config.detail_view_orientation;
        this.query = Immutable.fromJS({});
    }

    /**
     * Load Media and generate Filters
     * @param  {[type]} result [description]
     * @return {[type]}        [description]
     */
    onLoadMediaSuccess( result ) {
        let data = result.data;
        this._cache[ JSON.stringify( result.params ) ] = result;

        this.filters = Immutable.fromJS( data.filter );
        this.meta = Immutable.fromJS( data.meta );
        this.media = Immutable.fromJS( data.items );
        this.params = result.params;
        this.slotDefinitions = Immutable.fromJS(data["slot_definitions"]);

        let mediaItems = this.flagFirstAndLastElement(data);

        this.media = Immutable.fromJS( mediaItems );

        // Active Filters from selections
        var activeFilters = data.activeFilters.map(filter => filter.params.split('&').map(sub => {
            var obj = {}; obj.name = filter.name; obj.params = sub; obj.value = filter.value; return obj;
        }));
        activeFilters = flatten( activeFilters ).map( afilter => {
            var _afilter = { ...afilter };

            _afilter.params = encodeUrl(_afilter.params);
            _afilter.params = queryString.parse( _afilter.params );
            return _afilter;
        });

        // Active Filters from search
        if ( this.params.search_id ) {
            activeFilters.push({
                name: data.meta.search_name || 'SEARCH',
                params: { 'search_id': this.params.search_id }
            });
        }
        else if ( this.params.search ) {
            activeFilters.push({
                name: this.params.search,
                params: { 'search': this.params.search }
            });
        }

        this.selectedFilters = Immutable.fromJS( activeFilters );
    }

    onLoadMediaModalsSuccess( result ) {
        let data = result.data;
        this._cache[ JSON.stringify( result.params ) ] = result;

        // define which mediaItem is first and which one is last
        let mediaItems = this.flagFirstAndLastElement(data);

        this.modalMeta = Immutable.fromJS( data.meta );
        this.mediaModal = Immutable.fromJS( mediaItems );

        if (this.fetchPreviousPage) {
            this.fetchPreviousPage = false;
            setTimeout( () => {
                this.alt.getActions('modals').openMediaModal( this.mediaModal.get( this.mediaModal.size - 1) );
            }, 10 );
        }

        if (this.fetchNextPage) {
            this.fetchNextPage = false;
            setTimeout( () => {
                this.alt.getActions('modals').openMediaModal( this.mediaModal.get( 0 ) );
            }, 10 );
        }
    }

    flagFirstAndLastElement(data) {
        return data.items.map(function(currentValue, index) {
            currentValue.indexInResults = index;
            currentValue.firstElementOfResults = ( index == !!0);
            currentValue.lastElementOfResults = ( index == (data.items.length - 1) ? true : false);
            return currentValue;
        });
    }

    cloneMediaAndMetaToModalData() {
        // This two will be used for modal, because we need to be independent from rest of page
        this.mediaModal = this.media;
        this.modalMeta = this.meta;
        this.modalOrientation = window.config.detail_view_orientation;
        this.slotConfig = window.config.detail_view;
    }

    cloneMediaAndMetaToModalDataFromBasket(basket) {
        let mediaItems = this.flagFirstAndLastElement(basket.toJS());
        this.mediaModal = Immutable.fromJS( mediaItems );
        // we need to fake basket page as only page (because we use same layout as normal media page)
        this.modalMeta = Immutable.fromJS({
            total_records: mediaItems.length,
            per_page: mediaItems.length,
            page: 1,
        });
    }

    cloneMediaAndMetaToModalDataFromShared(shared) {
        let mediaItems = this.flagFirstAndLastElement(shared.toJS()[0]);
        this.mediaModal = Immutable.fromJS( mediaItems );
        // we need to fake basket page as only page (because we use same layout as normal media page)
        this.modalMeta = Immutable.fromJS({
            total_records: mediaItems.length,
            per_page: mediaItems.length,
            page: 1,
        });
    }

    cloneSlotDefinitionsToModalDataFromBasket(slotDefinitions) {
        this.modalOrientation = window.config.detail_view_orientation;
        this.slotConfig = window.config.detail_view;
        this.slotDefinitions = Immutable.fromJS(slotDefinitions);
    }

    cloneSlotDefinitionsToModalDataFromShared(slotDefinitions) {
        this.modalOrientation = window.config.shared_view_orientation;
        this.slotConfig = window.config.shared_view;
        this.slotDefinitions = Immutable.fromJS(slotDefinitions);
    }

    onLoadMediaError( err ) {
        if (error.status === 401) {
            api.removeToken();
            store.clear();
            cookie.remove('basicauth_token');
            window.location = `${ location.protocol }//${ location.host }`;
        }
    }

    onLoadLandingPageSuccess( landingPage ) {
        this.landingPage = Immutable.fromJS( landingPage );
    }

    onLoadLandingPageError(error) {
        if (error.status === 401) {
            api.removeToken();
            store.clear();
            cookie.remove('basicauth_token');
            window.location = `${ location.protocol }//${ location.host }`;
        }
    }

    onCheckQuotaSuccess( result ) {
        this.quotaExceeded = !result.success;
    }

    onCheckQuotaError( err ) {

    }

    onClearSearch() {
        delete this.params.search;
    }

    onCloseBasket( id ) {
        const ix = this.media.findIndex( item => item.get('id') === id );
        this.media = this.media.updateIn( [ ix, '_basket' ], active => false );
    }

    onClearQuery() {
        this.query = Immutable.fromJS( {} );
    }

    onUpdateFilterParams(params) {
        for (var property in params) {
            if (!params.hasOwnProperty(property)) {
                continue;
            }

            var val = params[property];
            if (val && val.length > 0) {
                this.query = this.query.set(property, val);
            } else {
                this.query = this.query.remove(property);
            }
        }
    }

    onToggleFilter( ix ) {
        this.filters = this.filters.updateIn( [ ix, 'active' ], active => !active );
    }

    onToggleItemSelection( id ) {
        const ix = this.media.findIndex( item => id === item.get('id') && false !== item.get('showdownload') );
        if ( ix > -1 ) {
            this.media = this.media.updateIn( [ ix, '_checked' ], active => !active );
        }
    }

    onToggleBasket( id ) {
        const ix = this.media.findIndex( item => item.get('id') === id );
        this.media = this.media.updateIn( [ ix, '_basket' ], active => !active );
    }

    onToggleAllItemSelection() {
        const eligible     = this.media.filter( item => false !== item.get('showdownload') );
        const countChecked = eligible.filter( item => item.get('_checked') === true ).size;
        const shouldCheck  = ( countChecked !== eligible.size );

        this.media = this.media.map( item => {
            if ( false !== item.get('showdownload') ) {
                item = item.set( '_checked', shouldCheck );
            }
            return item;
        });
    }

    onRemoveAllItemSelection() {
        this.media = this.media.map( item => item.set( '_checked', false ) );
    }

    onCloseFilters() {
        this.filters = this.filters.map( filter => filter.set( 'active', false ) );
    }

    toggleExpand() {
        this.isExpanded = !this.isExpanded;
        this.filters = this.filters.map( filter => filter.set( 'active', this.isExpanded ) );
    }

    toggleItemAddedNotification( data ) {
        const { mediaId, basketName } = data;
        const ix = this.media.findIndex( item => mediaId === item.get('id') );
        this.media = this.media.updateIn( [ ix, '_notification' ], notification => ( notification ? false : basketName ) );
    }

    onRemoveSelectedFilter( index ) {
        this.selectedFilters = this.selectedFilters.filter( ( filter, i ) => {
            return i !== index
        });
    }

    setPreviousElement( id ) {
        const ix = this.mediaModal.findIndex( item => item.get('id') === id );
        let indexOfElement = ix - 1;
        if (indexOfElement >= 0)
            this.previousElement = this.mediaModal.get( indexOfElement );
    }

    setNextElement( id ) {
        const ix = this.mediaModal.findIndex( item => item.get('id') === id );
        let indexOfElement = ix + 1;
        if (indexOfElement <= this.mediaModal.size)
            this.nextElement = this.mediaModal.get( indexOfElement );
    }

    openCurrentMedia( id ) {
        const ix = this.mediaModal.findIndex( item => item.get('id') === id );
        if (typeof(ix) !== 'undefined') {
            let currentMediaitem = this.mediaModal.get( ix );
            setTimeout( () => {
                this.alt.getActions('modals').openMediaModal( currentMediaitem );
            }, 10 );
        }
    }

    fetchPreviousPage() {
        this.fetchPreviousPage = true;
    }

    fetchNextPage() {
        this.fetchNextPage = true;
    }

    setLayout( layout ) {
        store.set('active_layout', layout);
    }

    onLoadError(error) {
        if (error.status === 401) {
            api.removeToken();
            store.clear();
            cookie.remove('basicauth_token');
            window.location = `${ location.protocol }//${ location.host }`;
        }
    }
}



// WEBPACK FOOTER //
// ./app/stores/mediaStore.js